import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { 
  Users, Globe, TrendingUp, Megaphone, Sparkles, Heart,
  Settings, MessageSquare, FileCheck, Play, CheckCircle,
  Shield, FileText, Lock, Mail, Phone, ArrowRight, Menu, X,
  Eye, Layers, Languages, Film, ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_PICTUREMARK_BLACK = "https://customer-assets.emergentagent.com/job_business-11/artifacts/0ycyaonz_MAGEDIGITAL_lg_picturemark_150dpi_b_3C.png";
const LOGO_PICTUREMARK_WHITE = "https://customer-assets.emergentagent.com/job_business-11/artifacts/eg6sx3ey_MAGEDIGITAL_lg_picturemark_150dpi_w_3C.png";
const LOGO_WORDMARK_BLACK = "https://customer-assets.emergentagent.com/job_business-11/artifacts/y40y9fsg_MAGEDIGITAL_lg_wordmark_150dpi_b_3C.png";
const LOGO_WORDMARK_WHITE = "https://customer-assets.emergentagent.com/job_business-11/artifacts/rrxmg5bu_MAGEDIGITAL_lg_wordmark_150dpi_w_3C.png";

// Logo Components
const LogoPicturemark = ({ variant = "black", className = "h-8" }) => (
  <img 
    src={variant === "white" ? LOGO_PICTUREMARK_WHITE : LOGO_PICTUREMARK_BLACK} 
    alt="MAGE Digital" 
    className={className}
  />
);

const LogoWordmark = ({ variant = "black", className = "h-6" }) => (
  <img 
    src={variant === "white" ? LOGO_WORDMARK_WHITE : LOGO_WORDMARK_BLACK} 
    alt="MAGE Digital" 
    className={className}
  />
);

// Navigation
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#problem", label: "The Problem" },
    { href: "#solution", label: "Solution" },
    { href: "#pilot", label: "Pilot Offer" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#FDFBF7]/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <LogoPicturemark variant="black" className="h-10 w-auto" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a 
                key={link.href} 
                href={link.href} 
                className="nav-link"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact" className="btn-primary" data-testid="nav-cta">
              Book Meeting
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#E5E5E5]" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a 
                key={link.href} 
                href={link.href} 
                className="block py-3 nav-link"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact" className="btn-primary mt-4 w-full justify-center" onClick={() => setIsOpen(false)}>
              Book Meeting
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center bg-[#FDFBF7]">
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 py-32">
        <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#404040] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          FEB 2026
        </p>
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold uppercase tracking-tighter leading-[0.9] mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span className="text-[#0A0A0A]">GEN</span>{" "}
          <span className="text-[#DC2626]">AI</span>
          <br />
          <span className="text-[#0A0A0A]">LOCALIZATION</span>
          <br />
          <span className="text-[#404040] text-3xl md:text-5xl lg:text-6xl font-semibold normal-case tracking-normal">
            for Your Campaign
          </span>
        </h1>
        <p className="text-lg md:text-xl text-[#404040] max-w-xl mb-10 leading-relaxed">
          Transform one master film into market-specific versions. 
          Different faces, same performance, zero reshoots.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a href="#pilot" className="btn-primary" data-testid="hero-cta-primary">
            Start Pilot <ArrowRight size={18} />
          </a>
        </div>
      </div>

      <a 
        href="#value" 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce"
        data-testid="scroll-indicator"
      >
        <ChevronDown size={32} className="text-[#404040]" />
      </a>
    </section>
  );
};

// Value Props Section
const ValueProps = () => {
  const props = [
    { 
      highlight: "ZERO", 
      text: "RESHOOTS NEEDED.",
      desc: "AI handles talent replacement seamlessly"
    },
    { 
      highlight: "FEW WEEKS", 
      text: "DELIVERY.",
      desc: "From master to market-ready in record time"
    },
    { 
      highlight: "ONE MASTER FILM", 
      text: "FOR EVERY MARKET.",
      desc: "Scale globally from a single production"
    },
  ];

  return (
    <section id="value" data-testid="value-section" className="py-24 md:py-32 bg-[#0A0A0A] text-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
          {props.map((prop, index) => (
            <div 
              key={index} 
              className="text-center md:text-left p-8 border-b md:border-b-0 md:border-r border-white/10 last:border-0"
              data-testid={`value-prop-${index}`}
            >
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-[#DC2626]">{prop.highlight}</span>
                <br />
                {prop.text}
              </h3>
              <p className="text-white/60 mt-4">{prop.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const stats = [
    { icon: Users, title: "1 in 3 Consumers", desc: "Is non-white - yet most campaigns still feature one dominant look." },
    { icon: Globe, title: "No single face fits all", desc: "From Latin America to Europe to Asia - every market has its own standard." },
    { icon: Sparkles, title: "4.5B people, zero consensus", desc: "Korean, Indian, Japanese, Chinese & Thai consumers each expect their own face." },
    { icon: Megaphone, title: "One campaign reaches a few", desc: "Brands lose relevance in every market they fail to represent." },
    { icon: TrendingUp, title: "Fastest growing consumer base", desc: "The most visually diverse region on earth - almost always ignored." },
    { icon: Heart, title: "Beauty ideals vary by nation", desc: "What resonates in Mexico City looks foreign in Buenos Aires." },
  ];

  return (
    <section id="problem" data-testid="problem-section" className="py-16 md:py-24 bg-[#FDFBF7]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="max-w-3xl mb-16">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#DC2626] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            THE PROBLEM
          </p>
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            A single campaign can't speak to{" "}
            <span className="text-[#DC2626]">every market, every culture, and every audience.</span>
          </h2>
          <p className="text-lg text-[#404040]">
            Producing tailored content for each, was until now prohibitively expensive and time-consuming.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="stat-card card-hover"
              data-testid={`problem-stat-${index}`}
            >
              <div className="icon-box mb-6">
                <stat.icon size={24} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stat.title}
              </h3>
              <p className="text-[#404040]">{stat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// What We Localize Section
const LocalizeSection = () => {
  const adaptable = [
    { icon: Eye, text: "On-screen faces and expression layer" },
    { icon: Languages, text: "Language and lip-sync layer" },
    { icon: Layers, text: "Cultural visual cues and selected scene elements" },
    { icon: Film, text: "Market/channel-specific output versions" },
    { icon: Sparkles, text: "Hero products" },
    { icon: Globe, text: "Background locations" },
  ];

  const protected_ = [
    { icon: FileText, text: "Core story and direction" },
    { icon: Shield, text: "Brand look and campaign intent" },
  ];

  return (
    <section id="solution" data-testid="solution-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="max-w-3xl mb-16">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#DC2626] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            OUR SOLUTION
          </p>
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            What Exactly We{" "}
            <span className="text-[#DC2626]">Localize</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-[#404040] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ADAPTABLE ELEMENTS
            </h3>
            <div className="space-y-5">
              {adaptable.map((item, index) => (
                <div key={index} className="flex items-center gap-4" data-testid={`adaptable-${index}`}>
                  <div className="w-10 h-10 flex items-center justify-center bg-[#DC2626]/10 text-[#DC2626]">
                    <item.icon size={20} />
                  </div>
                  <span className="text-[#0A0A0A]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-[#404040] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PROTECTED ELEMENTS
            </h3>
            <div className="space-y-5 mb-10">
              {protected_.map((item, index) => (
                <div key={index} className="flex items-center gap-4" data-testid={`protected-${index}`}>
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A]/10 text-[#0A0A0A]">
                    <item.icon size={20} />
                  </div>
                  <span className="text-[#0A0A0A]">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="p-6 bg-[#F5F5F0] border-l-4 border-[#DC2626]">
              <p className="font-bold uppercase tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                RESULT: <span className="text-[#DC2626]">Market-specific versions</span> without rebuilding the full production
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Delivery Model Section
const DeliverySection = () => {
  const steps = [
    { icon: Settings, title: "Feasibility in 48H", desc: "Shot-level check with go / adjust / alternative recommendation." },
    { icon: MessageSquare, title: "Scope Alignment", desc: "Markets, assets, legal boundaries, review flow." },
    { icon: FileCheck, title: "Production + QA", desc: "Localization execution + human post supervision." },
    { icon: Play, title: "Delivery + Revisions", desc: "Structured review loop and final handover. Typical timeline: 2 to 3 weeks." },
  ];

  return (
    <section data-testid="delivery-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-16">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#DC2626] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            DELIVERY MODEL
          </p>
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            From Brief to <span className="text-[#DC2626]">Delivery</span>
          </h2>
        </div>

        <div className="relative">
          <div className="hidden lg:block timeline-line" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative" data-testid={`delivery-step-${index}`}>
                <div className="hidden lg:flex justify-center mb-6">
                  <div className="timeline-dot" />
                </div>
                <div className="icon-box mb-4">
                  <step.icon size={24} />
                </div>
                <h3 className="text-lg font-bold uppercase mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-[#404040] text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Section
const TrustSection = () => {
  const columns = [
    {
      icon: CheckCircle,
      title: "Quality",
      items: [
        "Human QA on every shot",
        "Brand consistency checkpoints",
        "Client approval gates"
      ]
    },
    {
      icon: FileText,
      title: "Legal",
      items: [
        "Transparent usage model",
        "Clear talent and replacement rights setup",
        "Defined delivery and usage terms"
      ]
    },
    {
      icon: Lock,
      title: "Risk Control",
      items: [
        "Early shot-level feasibility",
        "Explicit technical boundaries before kickoff",
        "No surprise constraints at final delivery"
      ]
    },
  ];

  return (
    <section data-testid="trust-section" className="py-16 md:py-24 bg-[#F5F5F0]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quality, <span className="text-[#DC2626]">Legal</span>, & Risk Management
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {columns.map((col, index) => (
            <div 
              key={index} 
              className="bg-white p-8 border border-[#E5E5E5]"
              data-testid={`trust-col-${index}`}
            >
              <div className="icon-box mb-6">
                <col.icon size={24} />
              </div>
              <h3 className="text-xl font-bold uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {col.title}
              </h3>
              <ul className="space-y-4">
                {col.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[#DC2626] mt-0.5 flex-shrink-0" />
                    <span className="text-[#404040]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pilot Offer Section
const PilotSection = () => {
  const pilotSteps = [
    { icon: Mail, text: "Send Your Master Spot" },
    { icon: Settings, text: "We Assess Feasibility" },
    { icon: CheckCircle, text: "Get Pilot Setup & Sample" },
  ];

  return (
    <section id="pilot" data-testid="pilot-section" className="py-16 md:py-24 bg-[#0A0A0A] text-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#DC2626] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              GET STARTED
            </p>
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pilot Offer:{" "}
              <span className="text-[#DC2626]">Low-Risk Entry</span>
            </h2>
            <p className="text-lg text-white/70 mb-10">
              Start with one pilot to validate quality and speed before scaling.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              {pilotSteps.map((step, index) => (
                <div key={index} className="flex items-center gap-3" data-testid={`pilot-step-${index}`}>
                  <div className="w-10 h-10 flex items-center justify-center border border-white/30">
                    <step.icon size={20} />
                  </div>
                  <span className="text-sm uppercase tracking-wider">{step.text}</span>
                  {index < pilotSteps.length - 1 && (
                    <ArrowRight size={16} className="hidden sm:block text-white/40 ml-2" />
                  )}
                </div>
              ))}
            </div>

            <a href="#contact" className="btn-primary bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="pilot-cta">
              Start Your Pilot <ArrowRight size={18} />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="p-8 border border-white/20">
              <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-white/60 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                PILOT SCOPE
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                  1 Master Product Film
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                  1 Localized Market Version
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                  Fixed Process & Timeline
                </li>
              </ul>
            </div>

            <div className="p-8 border border-white/20">
              <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-white/60 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                WHAT CLIENT GETS
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                  Feasibility Assessment
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                  Localization Plan
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                  Delivery-Ready Sample Output
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section data-testid="about-section" className="py-16 md:py-24 bg-[#FDFBF7]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#DC2626] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            WHO WE ARE
          </p>
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            We Are <span className="text-[#DC2626]">MAGE DIGITAL</span>
          </h2>
          <p className="text-lg text-[#404040] mb-6 leading-relaxed">
            We are a team of commercial film specialists, directors, VFX artists, and AI developers. 
            We combine brand understanding with craft and technical precision, built for global rollouts 
            and local relevance.
          </p>
          <p className="text-lg text-[#404040] mb-10 leading-relaxed">
            With a trusted network of creatives, tech talent, and production partners, we deliver 
            scalable localization solutions—fast and production-safe.
          </p>
          <div className="flex items-center gap-4">
            <LogoPicturemark variant="black" className="h-12 w-auto" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const contacts = [
    { name: "JULIAN HOLLAND", email: "holland@mage.digital" },
    { name: "LEONARD LEIS", email: "leis@mage.digital" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#DC2626] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              LET'S TALK
            </p>
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ready to <span className="text-[#DC2626]">Localize</span>?
            </h2>
            <p className="text-lg text-[#404040] mb-10 leading-relaxed">
              Book a 30-minute meeting to discuss your campaign and see how we can help 
              you reach every market with the right face.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {contacts.map((contact, index) => (
                <div key={index} className="space-y-3">
                  <p className="text-sm text-[#404040] uppercase tracking-wider">Contact</p>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {contact.name}
                  </p>
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-3 text-[#0A0A0A] hover:text-[#DC2626] transition-colors" data-testid={`contact-email-${index}`}>
                    <Mail size={18} />
                    <span>{contact.email}</span>
                  </a>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#FDFBF7] p-8 md:p-12 border border-[#E5E5E5]">
            <div className="flex items-center gap-4 mb-8">
              <LogoPicturemark variant="black" className="h-10 w-auto" />
            </div>
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              30 Minute Meeting
            </h3>
            <p className="text-[#404040] mb-8">
              Discuss your campaign, explore possibilities, and get a custom feasibility assessment.
            </p>
            <a 
              href="mailto:holland@mage.digital?subject=Meeting%20Request%20-%20AI%20Localization" 
              className="btn-primary w-full justify-center bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="contact-cta"
            >
              BOOK DATE & TIME
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 bg-[#0A0A0A] text-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <LogoPicturemark variant="white" className="h-10 w-auto" />
          <p className="text-white/50 text-sm">
            © 2026 Mage Digital. Gen AI Localization for Global Campaigns.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Navigation />
      <Hero />
      <ValueProps />
      <ProblemSection />
      <LocalizeSection />
      <DeliverySection />
      <TrustSection />
      <PilotSection />
      <AboutSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
